import { formatMoney } from "@bw/shared/utils";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * Sticky action bar for the review flow: shows the BINDING total (from the quote)
 * and the primary action button. The total is always the server's quote number —
 * this bar never computes a price. The button label + disabled/busy state are
 * driven by the page (step-dependent: "Continue to payment" → "Review" →
 * "Confirm & pay").
 */
export function ConfirmBar({
  total,
  currency,
  actionLabel,
  onAction,
  disabled,
  busy,
  showTotal = true,
  pending = false,
}: {
  total: string | number | null | undefined;
  currency: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** Hide the total when another element (e.g. the quote summary) already owns it. */
  showTotal?: boolean;
  /**
   * The binding total is being recomputed (e.g. a player-count change fired a
   * fresh quote that has not settled). Show an explicit "Calculating…" instead
   * of a stale or em-dash figure, so the user never reads an old price as final
   * at the authorize moment.
   */
  pending?: boolean;
}) {
  return (
    <Paper
      elevation={8}
      sx={{
        // In document flow on phones (the compact bottom nav owns that edge and
        // the shell reserves clearance); sticky from desktop up.
        position: { xs: "static", md: "sticky" },
        bottom: { md: 0 },
        zIndex: 2,
        p: 1.5,
        mt: 2,
        borderRadius: 3,
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        {showTotal ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Total
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
            >
              {pending ? "Calculating…" : formatMoney(total, currency)}
            </Typography>
          </Box>
        ) : null}
        <Button
          type="button"
          variant="contained"
          onClick={onAction}
          disabled={disabled || busy}
          fullWidth={!showTotal}
          sx={{ borderRadius: 999, flexShrink: 0, "&.Mui-disabled": { pointerEvents: "auto" } }}
        >
          {busy ? "Working…" : actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}
